import React, { useState, useRef, useEffect, useCallback } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { api } from '../../utils/api'

const API_BASE = import.meta.env.VITE_API_URL || ''

// --- Constants ---
const MIN_IMAGES = 3
const MAX_IMAGES = 10
const POLL_INTERVAL_MS = 3000
const ACCEPTED_TYPES = ['image/jpeg', 'image/png']
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png'
const COMPRESS_THRESHOLD_MB = 20

const REQUIRED_SLOTS = [
  { key: 'front', label: 'Product Front' },
  { key: 'back', label: 'Product Back' },
  { key: 'serial', label: 'Model / Serial Number' },
]

// --- Theme ---
const theme = {
  green: '#16a34a',
  greenLight: '#dcfce7',
  greenBorder: '#86efac',
  grey50: '#f9fafb',
  grey100: '#f3f4f6',
  grey200: '#e5e7eb',
  grey300: '#d1d5db',
  grey400: '#9ca3af',
  grey500: '#6b7280',
  grey700: '#374151',
  grey900: '#111827',
  white: '#ffffff',
  red: '#ef4444',
  shadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
  shadowMd: '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.06)',
}

// --- Styles ---
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: theme.grey50,
    padding: '40px 16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  container: {
    width: '100%',
    maxWidth: 700,
    backgroundColor: theme.white,
    borderRadius: 12,
    boxShadow: theme.shadowMd,
    padding: '40px 32px',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: theme.grey900,
    textAlign: 'center',
    margin: 0,
  },
  subtitle: {
    fontSize: 16,
    color: theme.grey500,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 32,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.grey700,
    marginBottom: 12,
  },
  uploadArea: {
    border: `2px dashed ${theme.grey300}`,
    borderRadius: 8,
    padding: 24,
    textAlign: 'center',
    marginBottom: 24,
  },
  uploadButton: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: theme.grey100,
    color: theme.grey700,
    border: `1px solid ${theme.grey200}`,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  qrButton: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: theme.white,
    color: theme.grey700,
    border: `1px solid ${theme.grey200}`,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    marginLeft: 12,
    transition: 'background-color 0.15s',
  },
  thumbnailGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  thumbnail: {
    position: 'relative',
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: theme.shadow,
    cursor: 'grab',
    transition: 'transform 0.15s',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeButton: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: theme.white,
    border: 'none',
    fontSize: 12,
    lineHeight: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  slotsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  slot: {
    flex: '1 1 180px',
    minHeight: 140,
    border: `2px dashed ${theme.grey300}`,
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 12,
    backgroundColor: theme.grey50,
    transition: 'border-color 0.2s, background-color 0.2s, transform 0.2s',
    position: 'relative',
    overflow: 'hidden',
  },
  slotDragOver: {
    borderColor: theme.green,
    backgroundColor: theme.greenLight,
  },
  slotFilled: {
    borderColor: theme.greenBorder,
    borderStyle: 'solid',
    backgroundColor: theme.white,
  },
  slotLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.grey500,
    marginTop: 8,
  },
  slotImage: {
    width: '100%',
    height: 100,
    objectFit: 'cover',
    borderRadius: 6,
  },
  checkmark: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: theme.green,
    color: theme.white,
    fontSize: 13,
    lineHeight: '22px',
    textAlign: 'center',
    fontWeight: 700,
  },
  validationMessage: {
    fontSize: 13,
    color: theme.red,
    marginBottom: 16,
  },
  submitButton: {
    display: 'block',
    width: '100%',
    padding: '14px 0',
    backgroundColor: theme.green,
    color: theme.white,
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  submitButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  successContainer: {
    textAlign: 'center',
    padding: '40px 0',
  },
  successIcon: {
    fontSize: 48,
    color: theme.green,
    marginBottom: 16,
  },
  successText: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.grey900,
    marginBottom: 8,
  },
  startOverButton: {
    padding: '10px 24px',
    backgroundColor: theme.grey100,
    color: theme.grey700,
    border: `1px solid ${theme.grey200}`,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: 16,
  },
  qrContainer: {
    textAlign: 'center',
    marginTop: 16,
    padding: 20,
    backgroundColor: theme.grey50,
    borderRadius: 8,
    border: `1px solid ${theme.grey200}`,
  },
  qrExpiry: {
    fontSize: 12,
    color: theme.grey400,
    marginTop: 8,
  },
  qrLink: {
    fontSize: 13,
    color: theme.green,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    textDecoration: 'underline',
    marginTop: 8,
    display: 'inline-block',
  },
  helperText: {
    fontSize: 13,
    color: theme.grey400,
    marginTop: 8,
  },
  errorText: {
    fontSize: 13,
    color: theme.red,
    marginTop: 8,
  },
  videoButton: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: theme.white,
    color: theme.grey700,
    border: `1px solid ${theme.grey200}`,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    marginLeft: 12,
    transition: 'background-color 0.15s',
  },
  progressBar: {
    height: 6,
    backgroundColor: theme.grey200,
    borderRadius: 3,
    overflow: 'hidden',
    marginTop: 12,
  },
  progressFill: {
    height: '100%',
    backgroundColor: theme.green,
    borderRadius: 3,
    transition: 'width 0.3s',
  },
  keyframeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: 8,
    marginTop: 12,
  },
  keyframe: {
    position: 'relative',
    borderRadius: 6,
    overflow: 'hidden',
    cursor: 'pointer',
    border: `1px solid ${theme.grey200}`,
  },
  keyframeImage: {
    width: '100%',
    aspectRatio: '16/9',
    objectFit: 'cover',
    display: 'block',
  },
  keyframeLabel: {
    position: 'absolute',
    bottom: 4,
    left: 4,
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: theme.white,
    fontSize: 11,
    padding: '1px 6px',
    borderRadius: 4,
    fontWeight: 600,
  },
  keyframeActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  lightboxOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  lightboxImage: {
    maxWidth: '90vw',
    maxHeight: '85vh',
    borderRadius: 8,
  },
  lightboxClose: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: theme.white,
    border: 'none',
    fontSize: 20,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lightboxNav: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: theme.white,
    border: 'none',
    fontSize: 20,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lightboxCounter: {
    position: 'absolute',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    color: theme.white,
    fontSize: 14,
    backgroundColor: 'rgba(0,0,0,0.5)',
    padding: '4px 12px',
    borderRadius: 12,
  },
}

// ============================================================
// QuoteDummy Component
// ============================================================

function QuoteDummy() {
  // --- State ---
  const [photos, setPhotos] = useState([])
  const [slots, setSlots] = useState({ front: null, back: null, serial: null })
  const [dragOverSlot, setDragOverSlot] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  // Video state
  const [videoFile, setVideoFile] = useState(null)
  const [compressing, setCompressing] = useState(false)
  const [compressProgress, setCompressProgress] = useState(0)
  const [extractingFrames, setExtractingFrames] = useState(false)
  const [keyframes, setKeyframes] = useState([]) // base64 JPEG strings
  const [numKeyframes, setNumKeyframes] = useState(8)
  const [videoStatus, setVideoStatus] = useState('')
  const [lightboxFrame, setLightboxFrame] = useState(null)
  const videoInputRef = useRef(null)
  const nextLocalIdRef = useRef(-1) // negative IDs for client-side-only photos

  // QR session state
  const [qrToken, setQrToken] = useState(null)
  const [qrExpiry, setQrExpiry] = useState(null)
  const [qrLoading, setQrLoading] = useState(false)
  const [showQr, setShowQr] = useState(false)

  // Refs for polling
  const pollIntervalRef = useRef(null)
  const lastPhotoIdRef = useRef(0)

  // --- Video: FFmpeg + Keyframe Extraction ---
  async function loadScript(src) {
    if (document.querySelector(`script[src="${src}"]`)) return
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = src
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  async function getFFmpeg() {
    if (window._ffmpegLoaded && window._ffmpegInstance) return window._ffmpegInstance
    await loadScript('https://cdn.jsdelivr.net/npm/@ffmpeg/ffmpeg@0.12.10/dist/umd/ffmpeg.min.js')
    await loadScript('https://cdn.jsdelivr.net/npm/@ffmpeg/util@0.12.1/dist/umd/util.min.js')
    const { FFmpeg } = window.FFmpegWASM
    const ffmpeg = new FFmpeg()
    ffmpeg.on('progress', ({ progress }) => setCompressProgress(Math.round(progress * 100)))
    await ffmpeg.load({
      coreURL: 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.js',
      wasmURL: 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.wasm'
    })
    window._ffmpegInstance = ffmpeg
    window._ffmpegLoaded = true
    return ffmpeg
  }

  async function compressVideo(file) {
    const ffmpeg = await getFFmpeg()
    const { fetchFile } = window.FFmpegUtil
    const inputName = 'input' + (file.name.match(/\.\w+$/)?.[0] || '.mp4')
    const outputName = 'output.mp4'
    await ffmpeg.writeFile(inputName, await fetchFile(file))
    await ffmpeg.exec([
      '-i', inputName, '-vf', 'scale=-2:720',
      '-c:v', 'libx264', '-preset', 'fast', '-crf', '28',
      '-c:a', 'aac', '-b:a', '128k',
      '-movflags', '+faststart', '-y', outputName
    ])
    const data = await ffmpeg.readFile(outputName)
    return new File([data.buffer], 'compressed.mp4', { type: 'video/mp4' })
  }

  async function extractFrames(file, count) {
    const blobUrl = URL.createObjectURL(file)
    try {
      return await new Promise((resolve, reject) => {
        const video = document.createElement('video')
        video.muted = true
        video.preload = 'auto'
        const timeout = setTimeout(() => { video.src = ''; reject(new Error('Frame extraction timed out')) }, 30000)

        video.onloadedmetadata = () => {
          const duration = video.duration
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = Math.min(video.videoWidth, 1280)
          canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth))

          const frames = []
          const times = []
          for (let i = 0; i < count; i++) {
            times.push(count === 1 ? duration * 0.25 : (duration * i) / (count - 1))
          }

          let currentFrame = 0
          video.onseeked = () => {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
            frames.push(canvas.toDataURL('image/jpeg', 0.85).split(',')[1])
            currentFrame++
            setVideoStatus(`Extracting frame ${currentFrame}/${count}...`)
            if (currentFrame < times.length) {
              video.currentTime = times[currentFrame]
            } else {
              clearTimeout(timeout)
              video.src = ''
              resolve(frames)
            }
          }
          video.currentTime = times[0]
        }
        video.onerror = () => { clearTimeout(timeout); reject(new Error('Failed to load video')) }
        video.src = blobUrl
      })
    } finally {
      URL.revokeObjectURL(blobUrl)
    }
  }

  async function handleVideoUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    setVideoFile(file)
    setKeyframes([])
    setError(null)

    let processFile = file
    const sizeMB = file.size / (1024 * 1024)

    if (sizeMB > COMPRESS_THRESHOLD_MB) {
      setCompressing(true)
      setCompressProgress(0)
      setVideoStatus(`Compressing video (${sizeMB.toFixed(0)}MB)...`)
      try {
        processFile = await compressVideo(file)
        const newSizeMB = processFile.size / (1024 * 1024)
        setVideoStatus(`Compressed ${sizeMB.toFixed(0)}MB → ${newSizeMB.toFixed(1)}MB`)
      } catch (err) {
        console.error('Compression failed, using original:', err)
        setVideoStatus('Compression failed, using original file')
        processFile = file
      } finally {
        setCompressing(false)
      }
    }

    setExtractingFrames(true)
    setVideoStatus('Extracting keyframes...')
    try {
      const frames = await extractFrames(processFile, numKeyframes)
      setKeyframes(frames)
      setVideoStatus(`${frames.length} keyframes extracted — select frames to add as photos`)
    } catch (err) {
      console.error('Frame extraction failed:', err)
      setVideoStatus('Frame extraction failed: ' + err.message)
    } finally {
      setExtractingFrames(false)
    }
  }

  function addKeyframeAsPhoto(base64, frameIndex) {
    if (photos.length >= MAX_IMAGES) {
      setError(`Maximum ${MAX_IMAGES} images allowed`)
      return
    }
    const localId = nextLocalIdRef.current--
    setPhotos(prev => [...prev, {
      id: localId,
      url: `data:image/jpeg;base64,${base64}`,
      filename: `video-frame-${frameIndex + 1}.jpg`
    }].slice(0, MAX_IMAGES))
  }

  function addAllKeyframesAsPhotos() {
    const available = MAX_IMAGES - photos.length
    if (available <= 0) {
      setError(`Maximum ${MAX_IMAGES} images allowed`)
      return
    }
    const toAdd = keyframes.slice(0, available)
    const newPhotos = toAdd.map((base64, i) => ({
      id: nextLocalIdRef.current--,
      url: `data:image/jpeg;base64,${base64}`,
      filename: `video-frame-${i + 1}.jpg`
    }))
    setPhotos(prev => [...prev, ...newPhotos].slice(0, MAX_IMAGES))
    if (toAdd.length < keyframes.length) {
      setVideoStatus(`Added ${toAdd.length} of ${keyframes.length} frames (photo limit reached)`)
    } else {
      setVideoStatus(`Added ${toAdd.length} frames as photos`)
    }
  }

  function resetVideo() {
    setVideoFile(null)
    setKeyframes([])
    setVideoStatus('')
    setCompressing(false)
    setExtractingFrames(false)
    if (videoInputRef.current) videoInputRef.current.value = ''
  }

  const videoBusy = compressing || extractingFrames

  // --- Derived state ---
  const allSlotsFilled = REQUIRED_SLOTS.every(({ key }) => slots[key] !== null)
  const hasEnoughPhotos = photos.length >= MIN_IMAGES
  const canSubmit = hasEnoughPhotos && allSlotsFilled

  // --- QR Polling ---
  const startPolling = useCallback((token) => {
    stopPolling()
    pollIntervalRef.current = setInterval(async () => {
      try {
        const since = lastPhotoIdRef.current
        const data = await api.get(
          `/api/testing/quote-dummy/session/${token}/photos?since=${since}`
        )
        if (data.photos && data.photos.length > 0) {
          setPhotos(prev => {
            const existingIds = new Set(prev.map(p => p.id))
            const newPhotos = data.photos.filter(p => !existingIds.has(p.id))
            return [...prev, ...newPhotos].slice(0, MAX_IMAGES)
          })
          const maxId = Math.max(...data.photos.map(p => p.id))
          if (maxId > lastPhotoIdRef.current) {
            lastPhotoIdRef.current = maxId
          }
        }
      } catch {
        // Polling errors are non-critical; silently retry on next interval
      }
    }, POLL_INTERVAL_MS)
  }, [])

  function stopPolling() {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  // Clean up polling on unmount
  useEffect(() => {
    return () => stopPolling()
  }, [])

  // --- Handlers ---
  async function handleGenerateQr() {
    setQrLoading(true)
    setError(null)
    try {
      const data = await api.post('/api/testing/quote-dummy/session')
      setQrToken(data.sessionToken)
      setQrExpiry(data.expiresAt)
      setShowQr(true)
      startPolling(data.sessionToken)
    } catch (err) {
      setError(err.message || 'Failed to create QR session')
    } finally {
      setQrLoading(false)
    }
  }

  async function handleRegenerateQr() {
    stopPolling()
    setQrToken(null)
    setQrExpiry(null)
    await handleGenerateQr()
  }

  async function handleFileSelect(e) {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setError(null)
    setUploading(true)

    for (const file of files) {
      if (photos.length >= MAX_IMAGES) {
        setError(`Maximum ${MAX_IMAGES} images allowed`)
        break
      }
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError('Only JPG and PNG files are accepted')
        continue
      }

      const formData = new FormData()
      formData.append('photo', file)

      try {
        const res = await fetch(`${API_BASE}/api/testing/quote-dummy/upload`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        })
        const data = await res.json()
        if (!res.ok) {
          setError(data.error || 'Upload failed')
          break
        }
        setPhotos(prev => [...prev, data.photo].slice(0, MAX_IMAGES))
      } catch {
        setError('Upload failed. Please try again.')
        break
      }
    }

    setUploading(false)
    // Reset file input so the same file can be re-selected
    e.target.value = ''
  }

  function handleRemovePhoto(photoId) {
    setPhotos(prev => prev.filter(p => p.id !== photoId))
    // Clear any slots that referenced this photo
    setSlots(prev => {
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        if (next[key] === photoId) next[key] = null
      }
      return next
    })
  }

  // --- Drag and Drop ---
  function handleDragStart(e, photoId) {
    e.dataTransfer.setData('text/plain', String(photoId))
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleSlotDragOver(e, slotKey) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverSlot(slotKey)
  }

  function handleSlotDragLeave() {
    setDragOverSlot(null)
  }

  function handleSlotDrop(e, slotKey) {
    e.preventDefault()
    setDragOverSlot(null)
    const photoId = parseInt(e.dataTransfer.getData('text/plain'), 10)
    if (isNaN(photoId)) return

    // Verify the photo exists in our collection
    const photoExists = photos.some(p => p.id === photoId)
    if (!photoExists) return

    setSlots(prev => {
      const next = { ...prev }
      // Remove this photo from any other slot it might occupy
      for (const key of Object.keys(next)) {
        if (next[key] === photoId) next[key] = null
      }
      next[slotKey] = photoId
      return next
    })
  }

  function handleSubmit() {
    if (!canSubmit) return
    setSubmitted(true)
    stopPolling()
  }

  function handleStartOver() {
    setPhotos([])
    setSlots({ front: null, back: null, serial: null })
    setSubmitted(false)
    setError(null)
    setQrToken(null)
    setQrExpiry(null)
    setShowQr(false)
    lastPhotoIdRef.current = 0
    nextLocalIdRef.current = -1
    stopPolling()
    resetVideo()
  }

  // --- Helper: find photo by id ---
  function findPhoto(photoId) {
    return photos.find(p => p.id === photoId) || null
  }

  // --- Helper: format expiry ---
  function formatExpiry(expiresAt) {
    if (!expiresAt) return ''
    const expiry = new Date(expiresAt)
    const now = new Date()
    const diffMs = expiry - now
    if (diffMs <= 0) return 'Expired'
    const minutes = Math.ceil(diffMs / 60000)
    return `Expires in ${minutes} minute${minutes !== 1 ? 's' : ''}`
  }

  // --- Helper: build QR URL ---
  function buildQrUrl(token) {
    const origin = window.location.origin
    return `${origin}/mobile-quote-upload/${token}`
  }

  // ============================================================
  // Render: Success View
  // ============================================================
  if (submitted) {
    return (
      <div style={styles.page}>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <div style={styles.container}>
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>&#10003;</div>
            <div style={styles.successText}>
              Thank you! Your photos have been submitted for review.
            </div>
            <button
              style={styles.startOverButton}
              onClick={handleStartOver}
              onMouseEnter={e => { e.target.style.backgroundColor = theme.grey200 }}
              onMouseLeave={e => { e.target.style.backgroundColor = theme.grey100 }}
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ============================================================
  // Render: Main Form
  // ============================================================
  return (
    <div style={styles.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <div style={styles.container}>
        {/* Header */}
        <h1 style={styles.title}>Sell Your Music Gear for Cash</h1>
        <p style={styles.subtitle}>
          Upload photos of your item to receive a valuation
        </p>

        {/* Upload Area */}
        <div style={styles.uploadArea}>
          <div style={{ marginBottom: 12 }}>
            <label
              style={styles.uploadButton}
              onMouseEnter={e => { e.target.style.backgroundColor = theme.grey200 }}
              onMouseLeave={e => { e.target.style.backgroundColor = theme.grey100 }}
            >
              Choose Photos
              <input
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                multiple
                onChange={handleFileSelect}
                disabled={uploading || photos.length >= MAX_IMAGES}
                style={{ display: 'none' }}
              />
            </label>
            <button
              style={styles.qrButton}
              onClick={showQr ? null : handleGenerateQr}
              disabled={qrLoading}
              onMouseEnter={e => { e.target.style.backgroundColor = theme.grey100 }}
              onMouseLeave={e => { e.target.style.backgroundColor = theme.white }}
            >
              {qrLoading ? 'Generating...' : 'Upload from Phone'}
            </button>
            <label
              style={styles.videoButton}
              onMouseEnter={e => { e.target.style.backgroundColor = theme.grey100 }}
              onMouseLeave={e => { e.target.style.backgroundColor = theme.white }}
            >
              Upload Video
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                disabled={videoBusy}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          <div style={styles.helperText}>
            JPG or PNG only. Min {MIN_IMAGES}, max {MAX_IMAGES} images.
            {uploading && ' Uploading...'}
          </div>
          {error && <div style={styles.errorText}>{error}</div>}
        </div>

        {/* QR Code Section */}
        {showQr && qrToken && (
          <div style={styles.qrContainer}>
            <QRCodeSVG value={buildQrUrl(qrToken)} size={160} />
            <div style={styles.qrExpiry}>{formatExpiry(qrExpiry)}</div>
            <button style={styles.qrLink} onClick={handleRegenerateQr}>
              Generate new code
            </button>
          </div>
        )}

        {/* Video Processing */}
        {(videoFile || keyframes.length > 0) && (
          <div style={{ marginBottom: 24, padding: 20, backgroundColor: theme.grey50, borderRadius: 8, border: `1px solid ${theme.grey200}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={styles.sectionLabel}>
                Video Keyframes
                {videoFile && <span style={{ fontWeight: 400, color: theme.grey400 }}> — {videoFile.name}</span>}
              </div>
              {keyframes.length > 0 && (
                <button
                  style={{ background: 'none', border: 'none', color: theme.grey400, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
                  onClick={resetVideo}
                >
                  Clear video
                </button>
              )}
            </div>

            {/* Compression / extraction progress */}
            {videoBusy && (
              <div>
                {compressing && (
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${compressProgress}%` }} />
                  </div>
                )}
                <div style={{ ...styles.helperText, marginTop: 8 }}>
                  {extractingFrames && <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid ' + theme.grey300, borderTopColor: theme.green, borderRadius: '50%', animation: 'spin 0.8s linear infinite', verticalAlign: 'middle', marginRight: 8 }} />}
                  {videoStatus}
                </div>
              </div>
            )}
            {!videoBusy && videoStatus && (
              <div style={styles.helperText}>{videoStatus}</div>
            )}

            {/* Keyframe grid */}
            {keyframes.length > 0 && (
              <>
                <div style={styles.keyframeGrid}>
                  {keyframes.map((frame, i) => (
                    <div
                      key={i}
                      style={styles.keyframe}
                      onClick={() => setLightboxFrame(i)}
                    >
                      <img
                        src={`data:image/jpeg;base64,${frame}`}
                        alt={`Frame ${i + 1}`}
                        style={styles.keyframeImage}
                      />
                      <span style={styles.keyframeLabel}>{i + 1}</span>
                    </div>
                  ))}
                </div>
                <div style={styles.keyframeActions}>
                  <button
                    style={{ ...styles.uploadButton, backgroundColor: theme.green, color: theme.white, border: 'none' }}
                    onClick={addAllKeyframesAsPhotos}
                    disabled={photos.length >= MAX_IMAGES}
                  >
                    Add All as Photos
                  </button>
                  <div style={{ fontSize: 12, color: theme.grey400 }}>
                    Click a frame to preview, or click individual frames below to add one at a time
                  </div>
                </div>
                {/* Individual add buttons */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {keyframes.map((frame, i) => (
                    <button
                      key={i}
                      style={{ padding: '4px 10px', fontSize: 12, backgroundColor: theme.grey100, border: `1px solid ${theme.grey200}`, borderRadius: 4, cursor: 'pointer' }}
                      onClick={() => addKeyframeAsPhoto(frame, i)}
                      disabled={photos.length >= MAX_IMAGES}
                      title={`Add frame ${i + 1} as photo`}
                    >
                      + Frame {i + 1}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Lightbox for keyframe preview */}
        {lightboxFrame !== null && keyframes[lightboxFrame] && (
          <div style={styles.lightboxOverlay} onClick={() => setLightboxFrame(null)}>
            <img
              src={`data:image/jpeg;base64,${keyframes[lightboxFrame]}`}
              alt={`Frame ${lightboxFrame + 1}`}
              style={styles.lightboxImage}
              onClick={e => e.stopPropagation()}
            />
            <button style={styles.lightboxClose} onClick={() => setLightboxFrame(null)}>&times;</button>
            {lightboxFrame > 0 && (
              <button
                style={{ ...styles.lightboxNav, left: 16 }}
                onClick={e => { e.stopPropagation(); setLightboxFrame(lightboxFrame - 1) }}
              >&#8249;</button>
            )}
            {lightboxFrame < keyframes.length - 1 && (
              <button
                style={{ ...styles.lightboxNav, right: 16 }}
                onClick={e => { e.stopPropagation(); setLightboxFrame(lightboxFrame + 1) }}
              >&#8250;</button>
            )}
            <div style={styles.lightboxCounter}>
              {lightboxFrame + 1} / {keyframes.length}
            </div>
          </div>
        )}

        {/* Uploaded Thumbnails */}
        {photos.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={styles.sectionLabel}>
              Uploaded Photos ({photos.length}/{MAX_IMAGES})
            </div>
            <div style={styles.thumbnailGrid}>
              {photos.map(photo => (
                <div
                  key={photo.id}
                  style={styles.thumbnail}
                  draggable
                  onDragStart={e => handleDragStart(e, photo.id)}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)' }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
                >
                  <img
                    src={photo.url}
                    alt={photo.filename}
                    style={styles.thumbnailImage}
                  />
                  <button
                    style={styles.removeButton}
                    onClick={() => handleRemovePhoto(photo.id)}
                    title="Remove photo"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
            <div style={styles.helperText}>
              Drag photos into the required slots below
            </div>
          </div>
        )}

        {/* Required Image Slots */}
        <div style={{ marginBottom: 24 }}>
          <div style={styles.sectionLabel}>Required Photos</div>
          <div style={styles.slotsRow}>
            {REQUIRED_SLOTS.map(({ key, label }) => {
              const assignedPhoto = slots[key] ? findPhoto(slots[key]) : null
              const isDragOver = dragOverSlot === key
              const isFilled = assignedPhoto !== null

              const slotStyle = {
                ...styles.slot,
                ...(isDragOver ? styles.slotDragOver : {}),
                ...(isFilled ? styles.slotFilled : {}),
                ...(isFilled && !isDragOver ? { transform: 'scale(1)' } : {}),
              }

              return (
                <div
                  key={key}
                  style={slotStyle}
                  onDragOver={e => handleSlotDragOver(e, key)}
                  onDragLeave={handleSlotDragLeave}
                  onDrop={e => handleSlotDrop(e, key)}
                >
                  {isFilled ? (
                    <>
                      <img
                        src={assignedPhoto.url}
                        alt={label}
                        style={styles.slotImage}
                      />
                      <div style={styles.checkmark}>&#10003;</div>
                    </>
                  ) : (
                    <div style={{ color: theme.grey400, fontSize: 28 }}>+</div>
                  )}
                  <div style={styles.slotLabel}>{label}</div>
                </div>
              )
            })}
          </div>

          {/* Validation message */}
          {photos.length > 0 && !allSlotsFilled && (
            <div style={styles.validationMessage}>
              Please drag a photo into each required slot above
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          style={{
            ...styles.submitButton,
            ...(!canSubmit ? styles.submitButtonDisabled : {}),
          }}
          disabled={!canSubmit}
          onClick={handleSubmit}
          onMouseEnter={e => {
            if (canSubmit) e.target.style.opacity = '0.85'
          }}
          onMouseLeave={e => {
            if (canSubmit) e.target.style.opacity = '1'
          }}
        >
          Submit
        </button>
      </div>
    </div>
  )
}

export default QuoteDummy
